import type { IFactoryVersion } from '@shared/models/factory-element.model';
import type {
  Baseline,
  OneOfChildElements,
} from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import type { ResultsRecord } from '@shared/models/unit.model';
import { getResultsRecord } from '../results.helpers';
import {
  flattenElements,
  isDeletedElement,
  isElement,
} from '../recursive-element.helpers';
import { getResolvedBuildingVersion } from './resolved-version.helpers';
import type { ResolvedVersionEnrichContext } from './resolved-version-enrich-cache.helpers';

/**
 * True when {@link IProposal} already stores result rows that can be remapped onto a
 * new baseline. An empty {@link IProposal.resultsRecord} is not reusable — VersionAdd
 * should run enrich instead of skipping the worker.
 *
 * Does not require the cloned rows to cover every node in the resolved tree: category
 * re-enrichment often adds slots that have no proposal rows, and skipping enrich is
 * what keeps the new baseline numbers matching the proposal bar.
 */
export const hasClonedProposalResults = (proposal: IProposal): boolean =>
  Object.keys(proposal.resultsRecord).length > 0;

/**
 * Recursively drop soft-deleted elements (and their subtrees) from {@link elements}.
 * {@link IElement.isDeleted} is proposal-resolved view state (set by
 * `element.softDelete` actions in {@link getResolvedBuildingVersion}) and must never
 * be persisted on a baseline tree — leaked deleted elements made the save pipeline
 * silently skip server persistence (DEV-3641).
 */
const omitDeletedElements = (
  elements: OneOfChildElements[],
): OneOfChildElements[] =>
  elements
    .filter((element) => !isDeletedElement(element))
    .map((element) =>
      isElement(element)
        ? { ...element, elements: omitDeletedElements(element.elements) }
        : element,
    );

/**
 * Drop proposal ownership from a promoted subtree. {@link IElement.createdInProposalId}
 * and {@link IElement.importReplaceParent} belong to the proposal that imported
 * the group; on the new baseline the parent is an ordinary element.
 */
const withoutProposalOwnership = (
  element: OneOfChildElements,
): OneOfChildElements => {
  if (!isElement(element)) return element;
  const {
    createdInProposalId: _owner,
    importReplaceParent: _importParent,
    ...rest
  } = element;
  return { ...rest, elements: element.elements.map(withoutProposalOwnership) };
};

/**
 * Keep the cloned version-level row and rows for elements still in {@link keptElementIds}.
 * Soft-deleted (omitted) nodes are not remapped by VersionAdd, so leaving their keys
 * would persist orphan results on the new baseline.
 */
const omitUnkeptResultsRecordKeys = (
  resultsRecord: ResultsRecord,
  versionId: string,
  keptElementIds: ReadonlySet<string>,
): ResultsRecord => {
  const next: ResultsRecord = {};
  for (const [key, value] of Object.entries(resultsRecord)) {
    if (key === versionId || keptElementIds.has(key)) {
      next[key] = value;
    }
  }
  return next;
};

/**
 * Build a factory partial for a brand-new baseline version that equals {@link proposal}
 * merged onto {@link version}'s baseline tree.
 *
 * The resolved (merged) element tree — proposal actions applied + category re-enrichment,
 * see {@link getResolvedBuildingVersion} — becomes the new baseline, while every
 * proposal/action overlay is dropped. The returned factory contains one fresh,
 * action-free proposal seeded with the promoted baseline results. This keeps its
 * first rendered bar visually correct even when the cloned results let VersionAdd
 * skip enrichment. Name is left unset so {@link useAddVersion} /
 * {@link getNextVersionName} assign `Version N`.
 *
 * The returned partial is meant to be fed to the version-add flow (`useAddVersion` →
 * {@link addVersionToProject}), which regenerates ids, stamps the automatic name, and remaps
 * {@link Baseline.resultsRecord} keys. Results are cloned from the source
 * proposal via {@link getResultsRecord} (same as record-mode UI via
 * {@link useProposalResultsRecord}) — not recomputed, and not taken from
 * {@link Baseline.resultsRecord} on the source version. Keys for elements
 * omitted by {@link omitDeletedElements} are dropped so they cannot survive remap
 * as orphan rows. The source {@link Baseline.id} is intentionally kept so
 * that flow's id remapping behaves exactly like a normal version duplicate.
 *
 * @param version - The baseline version the proposal lives on.
 * @param proposal - The proposal to promote into a standalone baseline.
 * @param enrichContext - Project/weather/data-table context for resolve enrichment.
 */
export const buildBaselineVersionFromProposal = (
  version: Baseline,
  proposal: IProposal,
  enrichContext?: ResolvedVersionEnrichContext,
): Partial<IFactoryVersion> => {
  const resolved = getResolvedBuildingVersion(version, proposal, enrichContext);
  // Promoted elements are real baseline data now — no proposal owns them.
  const elements = omitDeletedElements(resolved.elements).map(
    withoutProposalOwnership,
  );
  const keptElementIds = new Set(
    flattenElements(...elements).map((element) => element.id),
  );

  // The merged tree IS the new baseline — strip the proposal/action overlay entirely.
  // Omit name so useAddVersion stamps Version N (not the source version's name).
  const { name: _sourceName, ...resolvedWithoutName } = resolved;
  const resultsRecord = omitUnkeptResultsRecordKeys(
    getResultsRecord(version, proposal),
    version.id,
    keptElementIds,
  );
  return {
    ...resolvedWithoutName,
    elements,
    proposals: [{ resultsRecord }],
    actions: [],
    resultsRecord,
  };
};
